import { memo, useState } from "react";
import { Box, Button, Card, Typography, styled, useTheme } from "@mui/material";
import noDataImg from "../../Images/noDataImg.png";
import Cpc from "./TableSkeleton";

const BoxWrapper = styled(Box)(({ theme }) => ({
  height: "100%",
  "& .MainHead": {
    "& h2": {
      color: theme.palette.mode === "dark" ? "#ffffff" : "#2A3547",
      fontSize: "18px",
      fontWeight: 700,
      lineHeight: "1",
    },
  },
  "& .KeywordDataWrapper": {
    marginTop: "18px",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "6px",
    "& .HeadData": {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      gap: "10px",
      padding: "12px 10px 10px",
      backgroundColor: theme.palette.primary.main + "2b",
      overflow: "hidden",
      borderRadius: "6px 6px 0 0",
      "& .keywords": {
        color: theme.palette.mode === "dark" ? "#ffffff" : "#2A3547",
        fontSize: "14px",
        fontWeight: 700,
        flex: "1",
        lineHeight: "1",
      },
      "& .SearchVolume": {
        color: theme.palette.mode === "dark" ? "#ffffff" : "#2A3547",
        fontSize: "14px",
        fontWeight: 700,
        width: "100px",
        textAlign: "right",
        lineHeight: "1",
      },
      "& .Difficulty": {
        color: theme.palette.mode === "dark" ? "#ffffff" : "#2A3547",
        fontSize: "14px",
        fontWeight: 700,
        width: "100px",
        textAlign: "right",
        lineHeight: "1",
      },
    },
    "& .BodyDataWrapper": {
      // Scroll removed
    },
    "& .BodyData": {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      gap: "10px",
      padding: "12px 10px 10px",
      borderBottom: `1px solid ${theme.palette.divider}`,
      "&:last-of-type": {
        borderBottom: "none",
      },
      "& .keywords": {
        color: theme.palette.primary.main,
        fontSize: "14px",
        fontWeight: 400,
        flex: "1",
        lineHeight: "20px",
        whiteSpace: "nowrap",
        width: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
      "& .SearchVolume": {
        color: theme.palette.mode === "dark" ? "#ffffff" : "#2A3547",
        fontSize: "14px",
        fontWeight: 400,
        width: "100px",
        textAlign: "right",
        lineHeight: "1",
      },
      "& .Difficulty": {
        color: theme.palette.mode === "dark" ? "#ffffff" : "#2A3547",
        fontSize: "14px",
        fontWeight: 400,
        width: "100px",
        textAlign: "right",
        lineHeight: "1",
      },
    },
  },
  "& .ShowMoreBtnWrapper": {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: "14px",
    gap: "5px",
    "& button": {
      lineHeight: 1,
      padding: "8px 12px 8px",
      "&:focus": {
        outline: "none",
      },
    },
  },
  "& .NoDataWrapper": {
    minHeight: "300px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    "& .ImgWrapper": {
      textAlign: "center",
      marginBottom: "10px",
      "& img": {},
    },
    "& .ContentWrapper": {
      "& h3": {
        color: theme.palette.mode === "dark" ? "#ffffff" : "#2A3547",
        textAlign: "center",
        marginBottom: "10px",
        fontSize: "16px",
        fontWeight: 600,
        lineHeight: "1",
      },
      "& p": {
        color: theme.palette.mode === "dark" ? "#ffffff" : "#2A3547",
        textAlign: "center",
        fontSize: "14px",
        marginBottom: "10px",
        "& span": {
          color: theme.palette.primary.main,
          fontWeight: 600,
        },
      },
    },
  },
}));

function KeywordIdea({ label, keywordsArray, loading = true }) {
  const theme = useTheme();
  const [startIndex, setStartIndex] = useState(0);
  const itemsPerPage = 5;
  const handleShowMore = () => {
    setStartIndex(prev => Math.min(prev + itemsPerPage, keywordsArray.length - itemsPerPage));
  };

  const handleShowLess = () => {
    setStartIndex(prev => Math.max(prev - itemsPerPage, 0));
  };

  const dynamicKeyword = keywordsArray?.[0] && Object.keys(keywordsArray[0]).find(key => key !== "geoName" && key !== "geoCode");
  return (
    <BoxWrapper>
      <Card
        sx={{
          padding: "24px 24px 24px 24px",
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: "none !important",
          height: "100%",
        }}
      >
        <Box className="MainHead">
          <Typography component="h2">{label}</Typography>
        </Box>
        {loading ? (
          <Cpc />
        ) : Array.isArray(keywordsArray) && keywordsArray.length > 0 ? (
          <>
            <Box className="KeywordDataWrapper">
              <Box className="HeadData">
                <Typography component="span" className="keywords">
                  GEONAME{" "}
                </Typography>
                <Typography component="span" className="SearchVolume">
                  GEO CODE
                </Typography>
                <Typography component="span" className="Difficulty">
                  {dynamicKeyword ? dynamicKeyword.toUpperCase() : "TRENDING"}
                </Typography>
              </Box>
              <Box className="BodyDataWrapper">
                {keywordsArray.slice(startIndex, startIndex + itemsPerPage).map((item, index) => (
                  <Box className="BodyData" key={item}>
                    <Typography component="span" className="keywords">
                      {item.geoName}
                    </Typography>
                    <Typography component="span" className="SearchVolume">
                      {item.geoCode}
                    </Typography>
                    <Typography component="span" className="Difficulty">
                      {item[dynamicKeyword] !== undefined ? item[dynamicKeyword] : "N/A"}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
            <Box className="ShowMoreBtnWrapper">
              <Button onClick={handleShowLess} disabled={startIndex === 0}>
                Show Less
              </Button>
              <Button onClick={handleShowMore} disabled={startIndex + itemsPerPage >= keywordsArray.length}>
                Show More
              </Button>
            </Box>
          </>
        ) : (
          <Box className="NoDataWrapper">
            <Box className="ImgWrapper">
              <Typography component="img" src={noDataImg} alt="No data available" />
            </Box>
            <Box className="ContentWrapper">
              <Typography component="h3">No data</Typography>
              <Typography component="p">
                We could not find any {label} for <Typography component="span">{dynamicKeyword || "keywords"}</Typography>
              </Typography>
            </Box>
          </Box>
        )}
      </Card>
    </BoxWrapper>
  );
}

export default memo(KeywordIdea);
